import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { AppTourService, ITourStep } from '../../service/app/tour.service';
import { Subject, takeUntil } from 'rxjs';
import { EmpresaService } from '@shared/service/app/empresa.service';

@Component({
    selector: 'app-tour',
    standalone: true,
    imports: [CommonModule, MatIconModule, MatButtonModule],
    templateUrl: './tour.component.html',
    styleUrls: ['./tour.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AppTourComponent implements OnInit, OnDestroy {
    active = false;
    currentStepIndex = 0;
    steps: ITourStep[] = [];
    currentStep: ITourStep | null = null;
    
    popoverStyle: any = {};
    highlightStyle: any = {};

    color_primario: string = '#000000';
    color_secundario: string = '#000000';

    private _pollingTimer: any;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _lastElement: HTMLElement | null = null;
    private _lastOriginalStyles: { zIndex: string, position: string } = { zIndex: '', position: '' };

    constructor(
        private _tourService: AppTourService,
        private _cdr: ChangeDetectorRef,
        private _empresaService: EmpresaService
    ) {}

    ngOnInit(): void {
        // Suscribirse a los colores de la empresa
        this._empresaService.empresa$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(empresa => {
                if (empresa) {
                    this.color_primario = empresa.color_primario;
                    this.color_secundario = empresa.color_secundario;
                }
            });

        // Suscribirse al estado del tour
        this._tourService.active$.pipe(takeUntil(this._unsubscribeAll)).subscribe(active => {
            this.active = active;
            if (active) {
                document.body.classList.add('tour-active');
                this.startPolling();
            } else {
                document.body.classList.remove('tour-active');
                this.stopPolling();
                this.removeHighlight();
            }
        });

        this._tourService.steps$.pipe(takeUntil(this._unsubscribeAll)).subscribe(steps => {
            this.steps = steps;
        });

        this._tourService.currentStep$.pipe(takeUntil(this._unsubscribeAll)).subscribe(index => {
            this.currentStepIndex = index;
            this.currentStep = this.steps[index];
            if (this.active) {
                this.startPolling(); // Reiniciar el seguimiento al cambiar de paso
            }
        });

        // Escuchar redimensionamiento de ventana para reposicionar
        window.addEventListener('resize', () => this.updatePosition());
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        this.stopPolling();
    }

    private startPolling(): void {
        this.stopPolling();
        this.updatePosition();
        
        // Ejecutar un re-cálculo frecuente durante los primeros 2 segundos 
        // para capturar movimientos de layout o sidebar
        let count = 0;
        this._pollingTimer = setInterval(() => {
            this.updatePosition();
            count++;
            if (count > 20) { // Después de 2 segundos (20 * 100ms), bajamos la frecuencia
                this.stopPolling();
                // Dejamos un polling muy lento por si acaso
                this._pollingTimer = setInterval(() => this.updatePosition(), 1000);
            }
        }, 100);
    }

    private stopPolling(): void {
        if (this._pollingTimer) {
            clearInterval(this._pollingTimer);
            this._pollingTimer = null;
        }
    }

    private removeHighlight(): void {
        if (this._lastElement) {
            this._lastElement.classList.remove('tour-highlight');
            // Restaurar estilos originales
            this._lastElement.style.zIndex = this._lastOriginalStyles.zIndex;
            this._lastElement.style.position = this._lastOriginalStyles.position;
            this._lastElement = null;
        }
    }

    updatePosition(): void {
        if (!this.active || !this.currentStep) return;

        const element = document.querySelector(this.currentStep!.elementId) as HTMLElement;
        if (!element) return;

        // Si el elemento cambió, restauramos el anterior antes de elevar el nuevo
        if (this._lastElement !== element) {
            this.removeHighlight();
            
            // Guardar estilos originales del nuevo elemento
            this._lastOriginalStyles = {
                zIndex: element.style.zIndex,
                position: element.style.position
            };
            this._lastElement = element;
        }

        element.classList.add('tour-highlight');
        
        // Elevar el elemento por encima del backdrop-blur (z-index del backdrop es 1140)
        element.style.zIndex = '1200';
        if (!element.style.position || element.style.position === 'static') {
            element.style.position = 'relative';
        }

        const rect = element.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Estilos para el resaltado
        this.highlightStyle = {
            top: `${rect.top - 6}px`,
            left: `${rect.left - 6}px`,
            width: `${rect.width + 12}px`,
            height: `${rect.height + 12}px`
        };

        // Dimensiones aproximadas del popover (340px ancho, ~250px alto estimado)
        const popoverWidth = 340;
        const popoverHeight = 300; 
        const margin = 20;

        let preferredPosition = this.currentStep.position || 'auto';

        // --- LÓGICA DE MEJOR POSICIÓN DINÁMICA (Auto Placement Inteligente) ---
        const spaceBottom = viewportHeight - (rect.bottom + margin);
        const spaceTop = rect.top - margin;
        const spaceRight = viewportWidth - (rect.right + margin);
        const spaceLeft = rect.left - margin;

        if (preferredPosition === 'auto') {
            // Evaluar cuál lado ofrece mayor amplitud y comodidad visual
            if (spaceBottom >= popoverHeight) {
                preferredPosition = 'bottom';
            } else if (spaceTop >= popoverHeight) {
                preferredPosition = 'top';
            } else if (spaceRight >= popoverWidth) {
                preferredPosition = 'right';
            } else if (spaceLeft >= popoverWidth) {
                preferredPosition = 'left';
            } else {
                // Si ninguno tiene espacio completo, elegir el que tenga el valor máximo absoluto
                const maxVertical = Math.max(spaceBottom, spaceTop);
                preferredPosition = maxVertical === spaceBottom ? 'bottom' : 'top';
            }
        } else {
            // Si tiene una posición fija sugerida pero colisiona, auto-flip a la mejor alternativa
            if (preferredPosition === 'bottom' && spaceBottom < popoverHeight) {
                preferredPosition = spaceTop >= popoverHeight ? 'top' : (spaceRight >= popoverWidth ? 'right' : 'left');
            } else if (preferredPosition === 'top' && spaceTop < popoverHeight) {
                preferredPosition = spaceBottom >= popoverHeight ? 'bottom' : (spaceRight >= popoverWidth ? 'right' : 'left');
            } else if (preferredPosition === 'left' && spaceLeft < popoverWidth) {
                preferredPosition = spaceRight >= popoverWidth ? 'right' : (spaceBottom >= popoverHeight ? 'bottom' : 'top');
            } else if (preferredPosition === 'right' && spaceRight < popoverWidth) {
                preferredPosition = spaceLeft >= popoverWidth ? 'left' : (spaceBottom >= popoverHeight ? 'bottom' : 'top');
            }
        }

        // --- CÁLCULO DE COORDENADAS FINALES ---

        let top = 0;
        let left = 0;

        switch (preferredPosition) {
            case 'top':
                top = rect.top - popoverHeight - margin;
                left = rect.left + (rect.width / 2) - (popoverWidth / 2);
                break;
            case 'bottom':
                top = rect.bottom + margin;
                left = rect.left + (rect.width / 2) - (popoverWidth / 2);
                break;
            case 'left':
                top = rect.top + (rect.height / 2) - (popoverHeight / 2);
                left = rect.left - popoverWidth - margin;
                break;
            case 'right':
                top = rect.top + (rect.height / 2) - (popoverHeight / 2);
                left = rect.left + rect.width + margin;
                break;
        }

        // --- AJUSTE DE BORDES (Safe Area) ---
        
        // Asegurar que no se salga por la izquierda
        if (left < margin) left = margin;
        // Asegurar que no se salga por la derecha
        if (left + popoverWidth > viewportWidth - margin) {
            left = viewportWidth - popoverWidth - margin;
        }
        // Asegurar que no se salga por arriba
        if (top < margin) top = margin;
        // Asegurar que no se salga por abajo
        if (top + popoverHeight > viewportHeight - margin) {
            top = viewportHeight - popoverHeight - margin;
        }

        this.popoverStyle = {
            top: `${top}px`,
            left: `${left}px`,
            width: `${popoverWidth}px`,
            position: 'fixed'
        };

        this._cdr.detectChanges();
    }

    next(): void {
        this._tourService.next();
    }

    previous(): void {
        this._tourService.previous();
    }

    stop(): void {
        this._tourService.stop();
    }
}
